import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { apiFootballFetch } from "@/lib/apiFootball.server";

/**
 * Resolve a real API-Football fixture id for a list of team-id pairs.
 *
 * For each pair the server calls `/fixtures/headtohead?h2h=A-B&next=1` to
 * find the next upcoming fixture between those two national teams. Live
 * fixtures take precedence over scheduled ones when both are returned.
 *
 * Returns a map keyed by the caller-supplied `id` (the app's synthetic
 * MatchInfo id, e.g. `mex-arg`). Missing/unresolved entries simply omit
 * the providerFixtureId so the caller can fall back to demo data.
 */

const Input = z.object({
  pairs: z
    .array(
      z.object({
        id: z.string().min(1).max(64),
        homeId: z.number().int().positive(),
        awayId: z.number().int().positive(),
      }),
    )
    .max(20),
});

interface AfFixtureItem {
  fixture?: { id?: number; date?: string; status?: { short?: string } };
  teams?: {
    home?: { id?: number };
    away?: { id?: number };
  };
}

export interface ProviderFixtureResolution {
  providerFixtureId: number | null;
  status: string | null;
  kickoff: string | null;
}

function pickBest(items: AfFixtureItem[]): AfFixtureItem | undefined {
  if (items.length === 0) return undefined;
  const live = ["1H", "HT", "2H", "ET", "BT", "P", "LIVE", "INT"];
  const liveItem = items.find((i) => live.includes(i.fixture?.status?.short ?? ""));
  if (liveItem) return liveItem;
  // Otherwise return the soonest upcoming (api-football returns chronological with next=1).
  return items[0];
}

export const resolveProviderFixtures = createServerFn({ method: "POST" })
  .inputValidator((input) => Input.parse(input))
  .handler(async ({ data }) => {
    const out: Record<string, ProviderFixtureResolution> = {};
    await Promise.all(
      data.pairs.map(async (p) => {
        try {
          // `next=N` only returns Not-Started fixtures and `last=N` returns
          // finished (FT) ones — both silently drop the match while it is
          // in progress. Query the live feed for each team in parallel with
          // recent + upcoming H2H, then let pickBest() prioritise LIVE > NS.
          const [rLive, rLast, rNext] = await Promise.all([
            apiFootballFetch(`/fixtures?live=all&team=${p.homeId}`),
            apiFootballFetch(
              `/fixtures/headtohead?h2h=${p.homeId}-${p.awayId}&last=3`,
            ),
            apiFootballFetch(
              `/fixtures/headtohead?h2h=${p.homeId}-${p.awayId}&next=1`,
            ),
          ]);
          if (!rLive.ok && !rLast.ok && !rNext.ok) {
            out[p.id] = { providerFixtureId: null, status: null, kickoff: null };
            return;
          }
          const liveItemsRaw =
            (rLive.data as { response?: AfFixtureItem[] } | null)?.response ?? [];
          // Keep only live fixtures that actually involve BOTH teams of the pair.
          const liveItems = liveItemsRaw.filter((it) => {
            const h = it.teams?.home?.id;
            const a = it.teams?.away?.id;
            return (
              (h === p.homeId && a === p.awayId) ||
              (h === p.awayId && a === p.homeId)
            );
          });
          const lastItems =
            (rLast.data as { response?: AfFixtureItem[] } | null)?.response ?? [];
          const nextItems =
            (rNext.data as { response?: AfFixtureItem[] } | null)?.response ?? [];
          const items = [...liveItems, ...nextItems, ...lastItems];
          const best = pickBest(items);
          out[p.id] = {
            providerFixtureId: best?.fixture?.id ?? null,
            status: best?.fixture?.status?.short ?? null,
            kickoff: best?.fixture?.date ?? null,
          };
        } catch {
          out[p.id] = { providerFixtureId: null, status: null, kickoff: null };
        }
      }),
    );
    return out;
  });